import { getCourseDatesFromCourseEnrolmentScheduleItems } from '@duke-hq/lib/dates';
import type {
  StudentWorkSchedule,
  StudentCourseEnrolmentScheduleItem,
} from '@duke-hq/svc-api-db/client';
import type { ProviderInvoiceRuleView, CounterPeriod } from '@duke-hq/billing/types/rules';
import { PRICE_RULE_TYPES } from '@duke-hq/billing/helpers/rules';

/** ---------- Decimal helpers (exact, 6dp scale) ---------- */
const SCALE = 1_000_000n;

function dec(n: string | number): bigint {
  const s = String(n);
  if (!s.includes('.')) {
    return BigInt(s) * SCALE;
  }
  const [i, f] = s.split('.');
  const frac = ((f ?? '') + '000000').slice(0, 6);
  const intPart = i != null && i.trim() !== '' ? i : '0';
  const fracPart = frac != null && frac.trim() !== '' ? frac : '0';
  return BigInt(intPart) * SCALE + BigInt(fracPart);
}
// const add = (a: bigint, b: bigint) => a + b;
const sub = (a: bigint, b: bigint) => a - b;
const mul = (a: bigint, b: bigint) => (a * b) / SCALE;
const div = (a: bigint, b: bigint) => (a * SCALE) / b;
const max0 = (a: bigint) => (a < 0n ? 0n : a);
const toStr = (a: bigint): string => {
  const sign = a < 0n ? '-' : '';
  const abs = a < 0n ? -a : a;
  const i = abs / SCALE;
  const f = String(abs % SCALE).padStart(6, '0');
  return `${sign}${i}.${f}`;
};

export function currentPeriodStart(now: Date, period: CounterPeriod): Date {
  const y = now.getUTCFullYear();
  switch (period) {
    case 'LIFETIME':
      return new Date(0);
    case 'YEAR':
      return new Date(Date.UTC(y, 0, 1));
    case 'MONTH':
      return new Date(Date.UTC(y, now.getUTCMonth(), 1));
    case 'WEEK': {
      const d = new Date(Date.UTC(y, now.getUTCMonth(), now.getUTCDate()));
      const day = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() - (day - 1));
      d.setUTCHours(0, 0, 0, 0);
      return d;
    }
  }
}

export type EnrolmentCtx = {
  contractType?: string | null;
  contractCreatedAt?: Date | null;
};

const matchesOptionalEq = (
  value: string | null | undefined,
  ruleValue: string | null | undefined,
) => (ruleValue == null ? true : value != null && value === ruleValue);

const matchesOptionalRange = (
  value: Date | null | undefined,
  from?: Date | null,
  to?: Date | null,
) => {
  if (from == null && to == null) {
    return true;
  } // no restriction
  if (!value) {
    return false;
  } // rule requires a date, enrolment has none
  if (from && value < from) {
    return false;
  }
  if (to && value > to) {
    return false;
  }
  return true;
};

/** All optional: null in rule means no restriction. */
export function ruleMatchesContext(
  rule: ProviderInvoiceRuleView,
  ctx: { courseId?: string | null; enrolment?: EnrolmentCtx },
): boolean {
  // course filter (optional)
  if (rule.courseId != null && rule.courseId !== ctx.courseId) {
    return false;
  }

  // enrolment type (optional)
  if (
    !matchesOptionalEq(
      getShortFormContractType(ctx.enrolment?.contractType ?? '') ?? null,
      rule.enrolmentContractType,
    )
  ) {
    return false;
  }

  // enrolment createdAt range (optional; inclusive)
  if (
    !matchesOptionalRange(
      ctx.enrolment?.contractCreatedAt ?? null,
      rule.enrolmentContractCreatedFrom ?? null,
      rule.enrolmentContractCreatedTo ?? null,
    )
  ) {
    return false;
  }

  return true;
}

type Enrolment = {
  id: number;
  course_segment: string;
  semester_2_start_at_date: Date | null;
  start_at_date: Date | string;
  work_schedule: StudentWorkSchedule;
  courseEnrolmentScheduleItems?: StudentCourseEnrolmentScheduleItem[];
};

export function computeInvoiceAndDueDatesForContext(
  rules: ProviderInvoiceRuleView[],
  isSecondSemester: boolean,
  enrolment: Enrolment,
) {
  let secondSemesterStartDate: Date | null = null;

  if (isSecondSemester) {
    if (enrolment.semester_2_start_at_date) {
      secondSemesterStartDate = enrolment.semester_2_start_at_date;
    } else {
      const items: StudentCourseEnrolmentScheduleItem[] =
        enrolment?.courseEnrolmentScheduleItems ?? [];

      const courseStartAndEndDates = getCourseDatesFromCourseEnrolmentScheduleItems({
        courseEnrolmentScheduleItems: items.map(
          ({ admin_course_schedule_week, semester, scheduled_due_date, item_type }) => ({
            admin_course_schedule_week:
              typeof admin_course_schedule_week === 'number'
                ? admin_course_schedule_week
                : Number(admin_course_schedule_week),
            semester: typeof semester === 'number' ? semester : Number(semester),
            scheduled_due_date,
            item_type,
          }),
        ),
        courseSegment: enrolment.course_segment,
        workSchedule: enrolment.work_schedule,
        startAt: enrolment.start_at_date,
      });
      const s2 = courseStartAndEndDates.semesters.find((s) => s.semester === 2)?.semesterStartDate
        .rawDate;
      secondSemesterStartDate = s2 ?? null;
    }
  }

  const invoiceDateInput =
    isSecondSemester && secondSemesterStartDate ? new Date(secondSemesterStartDate) : new Date();

  return deriveInvoiceAndDueDates(rules, invoiceDateInput);
}

export function deriveInvoiceAndDueDates(
  rules: ProviderInvoiceRuleView[],
  baseNowUTC = new Date(),
): { invoiceDate: Date; dueDate: Date } {
  let invoiceDate: Date | undefined; //= override?.invoiceDate;
  let dueDate: Date | undefined; //= override?.dueDate;

  // ---- INVOICE_DAY_OF_MONTH selection (never set in the past) ----
  if (!invoiceDate) {
    const year = baseNowUTC.getUTCFullYear();
    const month = baseNowUTC.getUTCMonth();
    const todayDay = baseNowUTC.getUTCDate();
    const maxDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

    const dayOfMonthRules = rules.filter(
      (r): r is ProviderInvoiceRuleView & { type: 'INVOICE_DAY_OF_MONTH'; dayOfMonth: number } =>
        r.type === 'INVOICE_DAY_OF_MONTH' && typeof r.dayOfMonth === 'number',
    );

    // Clamp each rule day into 1..maxDay, collect ascending
    const allDays = dayOfMonthRules
      .map((r) => Math.max(1, Math.min(r.dayOfMonth, maxDay)))
      .sort((a, b) => a - b);

    if (allDays.length > 0) {
      // Choose the smallest configured day that is >= today
      const candidate = allDays.find((d) => d >= todayDay);
      if (candidate != null) {
        invoiceDate = new Date(Date.UTC(year, month, candidate));
      } else {
        // All configured days have passed this month → use earliest configured day next month
        const nextMonthMaxDay = new Date(Date.UTC(year, month + 2, 0)).getUTCDate();
        const rawDays = dayOfMonthRules.map((r) => r.dayOfMonth).sort((a, b) => a - b);
        const smallest = rawDays[0] ?? 1;
        const nextDay = Math.max(1, Math.min(smallest, nextMonthMaxDay));
        invoiceDate = new Date(Date.UTC(year, month + 1, nextDay));
      }
    } else {
      // No day-of-month rules → default to today
      invoiceDate = new Date(Date.UTC(year, month, todayDay));
    }
  }
  const netRule = rules.find(
    (r): r is ProviderInvoiceRuleView & { type: 'DUE_TERMS_NET_DAYS'; netDays: number } =>
      r.type === 'DUE_TERMS_NET_DAYS' && typeof r.netDays === 'number' && r.netDays >= 0,
  );

  if (dueDate == null && invoiceDate != null && netRule != null) {
    // 86_400_000 = ms per day
    dueDate = new Date(invoiceDate.getTime() + netRule.netDays * 86_400_000);
  }

  // Fallback 14 days if still missing
  if (!dueDate) {
    dueDate = new Date(invoiceDate.getTime() + 14 * 86_400_000);
  }

  return { invoiceDate, dueDate };
}

type ComputeOpts = {
  courseId?: string | null;
  enrolment?: EnrolmentCtx;
};

export function computeFinalUnitAmount(
  basePrice: string,
  rules: ProviderInvoiceRuleView[],
  firstNApplied: Set<string>,
  enrolmentCourseSegment: string,
  opts?: ComputeOpts,
): string {
  let price = dec(basePrice);

  for (const r of rules) {
    if (!PRICE_RULE_TYPES.has(r.type)) {
      continue;
    }
    if (
      !ruleMatchesContext(r, {
        courseId: opts?.courseId ?? null,
        enrolment: opts?.enrolment,
      })
    ) {
      continue; // skip rules that don't match optional filters
    }

    switch (r.type) {
      case 'PRICE_OVERRIDE':
        if (r.priceOverride != null && r.priceOverride !== '') {
          price = dec(r.priceOverride);
        }
        break;

      case 'DISCOUNT_PERCENT':
        if (r.discountPercent != null && r.discountPercent !== '') {
          const pct = div(dec(r.discountPercent), dec(100));
          price = mul(price, sub(dec(1), pct));
        }
        break;

      case 'DISCOUNT_FIXED':
        if (r.discountFixed != null && r.discountFixed !== '') {
          price = max0(sub(price, dec(r.discountFixed)));
        }
        break;

      case 'FIRST_N_PRICE':
        if (firstNApplied.has(r.id) && r.priceOverride != null && r.priceOverride !== '') {
          price = dec(r.priceOverride);
        }
        break;

      case 'FIRST_N_DISCOUNT_PERCENT':
        if (firstNApplied.has(r.id) && r.discountPercent != null && r.discountPercent !== '') {
          const pct = div(dec(r.discountPercent), dec(100));
          price = mul(price, sub(dec(1), pct));
        }
        break;

      case 'FIRST_N_DISCOUNT_FIXED':
        if (firstNApplied.has(r.id) && r.discountFixed != null && r.discountFixed !== '') {
          price = max0(sub(price, dec(r.discountFixed)));
        }
        break;
      // To handle a switch is not exhaustive. Cases not matched. No effect on price calculation.
      case 'INVOICE_DAY_OF_MONTH':
      case 'DUE_TERMS_NET_DAYS':
        break;
    }
  }

  // Calculate semester based price after applying all rules
  if (
    enrolmentCourseSegment === 'First semester only' ||
    enrolmentCourseSegment === 'Second semester only'
  ) {
    price = div(price, dec(2));
  }

  return toStr(price);
}

export function getShortFormContractType(contractType: string) {
  switch (contractType) {
    case 'Workforce Australia':
      return 'WFA';
    case 'Parents Next':
      return 'PN';
    case 'Parents Pathways':
      return 'PP';
    default:
      return contractType;
  }
}

export function getEnrolmentCtx(
  enrolmentContractType: string | null | undefined,
  enrolmentContractCreatedAt: string | Date | null | undefined,
): EnrolmentCtx {
  return {
    contractType: enrolmentContractType ?? null,
    contractCreatedAt:
      typeof enrolmentContractCreatedAt === 'string' && enrolmentContractCreatedAt.trim() !== ''
        ? new Date(enrolmentContractCreatedAt)
        : enrolmentContractCreatedAt instanceof Date
          ? enrolmentContractCreatedAt
          : null,
  };
}

export function buildInvoiceId(enrolmentId: number, isSecondSemester: boolean) {
  return `INV-${String(enrolmentId).padStart(6, '0')}${isSecondSemester ? '-2' : ''}`;
}

export function buildItemCode(
  identifier?: string | null,
  specialisationShort?: string | null,
  courseSegment?: string | null,
) {
  return `${identifier ?? ''} ${specialisationShort ?? ''} ${courseSegment ?? ''}`.trim();
}

export function buildTracking(state?: string | null, enrolmentContractType?: string | null) {
  return [
    { name: 'Employee Group', option: state ?? undefined },
    { name: 'Contract', option: getShortFormContractType(enrolmentContractType ?? '') },
  ];
}

type CourseDate = {
  short: string;
  full?: string;
};

type CourseStartAndEndDates = {
  courseStartDate: CourseDate;
  courseEndDate?: CourseDate;
};

type EnrolmentItem = {
  course_identifier: string;
  course_title: string;
  enrolment_contract_type?: string;
  enrolment_course_segment?: string;
  enrolment_purchase_order?: string;
  courseStartAndEndDates: CourseStartAndEndDates;
  student_first_name: string;
  student_last_name: string;
  provider_first_name: string;
  provider_last_name: string;
  provider_site: string;
};

export function buildDescription(item: EnrolmentItem): string {
  const { courseStartDate, courseEndDate } = item.courseStartAndEndDates;
  const dateRange = `(${courseStartDate.short} - ${courseEndDate?.short ?? 'Missing end date'})`;
  const semester =
    typeof item.enrolment_course_segment === 'string' &&
    item.enrolment_course_segment.trim().length > 0
      ? `- ${item.enrolment_course_segment}`
      : '';
  const purchaseOrder =
    typeof item.enrolment_purchase_order === 'string' &&
    item.enrolment_purchase_order.trim().length > 0
      ? `(Purchase Order: ${item.enrolment_purchase_order})`
      : '';

  const parts = [
    item.course_identifier,
    item.course_title,
    getShortFormContractType(item.enrolment_contract_type ?? ''),
    semester,
    dateRange,
    '-',
    item.student_first_name,
    item.student_last_name,
    'enrolled by',
    item.provider_first_name,
    item.provider_last_name,
    `(${item.provider_site}).`,
    purchaseOrder,
  ];

  return parts.filter(Boolean).join(' ');
}
